import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageEvent } from '@angular/material/paginator';
import { Client } from 'app/models/client.models';
import { Marca } from 'app/models/marca.models';
import { Rubro } from 'app/models/rubro.models';
import { ClientesService } from 'app/services/clientes.service';
import { ParametrosService } from 'app/services/parametros.service';
import { ProductoService } from 'app/services/producto.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-precios-masivos',
  templateUrl: './precios-masivos.component.html',
  styleUrls: ['./precios-masivos.component.css'],
})
export class PreciosMasivosComponent implements OnInit {
  form: FormGroup;
  marcas: Marca[] = [];
  rubros: Rubro[] = [];
  proveedores: Client[] = [];
  marcasSelected = [];
  rubrosSelected = [];
  proveedoreSelected = [];
  
  // Estado centralizado para API de previsualización
  isLoading: boolean = false;
  previewData: any = null; // Datos completos de la API
  metadata: any = null; // Metadatos de paginación y totales
  previewItems: any[] = []; // Array de items calculados por el backend
  error: string | null = null;
  hasApiError = false; // Error real de conexión/API
  hasEmptyResults = false; // Respuesta exitosa pero sin resultados
  
  // Variables para paginación (controlada por backend)
  currentPage: number = 0;
  pageSize: number = 20; // 20 registros por defecto según las instrucciones
  searchQuery: string = '';
  
  // Variables para UI
  showPreview: boolean = false;
  
  // NUEVA VARIABLE: Estado de colapso para filtros estratégicos
  isStrategicFiltersExpanded: boolean = false; // Por defecto cerrado
  
  // NUEVA VARIABLE: Mensaje de error para validación de rango de márgenes
  marginRangeErrorMessage: string | null = null;

  // NUEVAS VARIABLES: Estados para la ejecución de actualización masiva
  isExecuting: boolean = false; // Estado de ejecución en curso
  executionProgress: string = ''; // Mensaje de progreso
  showConfirmationModal: boolean = false; // Control del modal de confirmación
  
  // Debounce timer para optimizar llamadas
  private debounceTimer: any = null;

  constructor(
    private _fb: FormBuilder,
    private parametroServicio: ParametrosService,
    private clienteService: ClientesService,
    private articuloService: ProductoService,
    private _snackBar: MatSnackBar
  ) {
    this.form = this._fb.group({
      tipoItem: ['ambos', Validators.required], // productos, servicios, ambos
      metodoActualizacion: ['precioVenta', Validators.required], // precioVenta, margenGanancia
      tipoAumento: ['porcentaje'], // porcentaje, monto
      proveedor: [null],
      rubro: [null],
      marca: [null],
      porcentaje: [0, [Validators.required, Validators.min(-100), Validators.max(999)]],
      mostrarDesglose: [false], // Control para mostrar tabla de auditoría completa
      
      // NUEVOS FILTROS ESTRATÉGICOS
      productType: ['all'], // all, commercializable, supplies
      marginRangeMin: ['', [Validators.min(0), Validators.max(999)]], // Margen mínimo en % - VACÍO por defecto
      marginRangeMax: ['', [Validators.min(0), Validators.max(999)]]  // Margen máximo en % - VACÍO por defecto
    });

    // Subscripciones para actualizar preview y limpiar filtros según tipo de ítem
    this.form.controls['tipoItem'].valueChanges.subscribe(tipo => {
      // Manejar estado disabled de controles
      if (tipo === 'servicios') {
        // Para servicios: solo habilitar proveedor, deshabilitar rubros y marcas
        this.form.get('proveedor')?.enable();
        this.form.get('rubro')?.disable();
        this.form.get('marca')?.disable();
        
        // NUEVO: Deshabilitar y limpiar filtro productType para servicios
        this.form.get('productType')?.disable();
        this.form.patchValue({ 
          rubro: null, 
          marca: null,
          productType: 'all' // Resetear a valor por defecto
        });
        this.rubrosSelected = [];
        this.marcasSelected = [];
      } else {
        // Habilitar todos los controles para productos y ambos
        this.form.get('proveedor')?.enable();
        this.form.get('rubro')?.enable();
        this.form.get('marca')?.enable();
        
        // NUEVO: Habilitar filtro productType para productos y ambos
        this.form.get('productType')?.enable();
      }
      // Resetear estado y obtener nueva previsualización
      this.resetPreviewState();
      setTimeout(() => {
        this.callPreviewAPI();
      }, 100);
    });

    this.form.controls['rubro'].valueChanges.subscribe(rubros => {
      this.rubrosSelected = rubros ? Array.from(rubros) : [];
      this.resetPreviewState();
      this.debouncedPreviewCall();
    });
    
    this.form.controls['marca'].valueChanges.subscribe(marcas => {
      this.marcasSelected = marcas ? Array.from(marcas) : [];
      this.resetPreviewState();
      this.debouncedPreviewCall();
    });
    
    this.form.controls['proveedor'].valueChanges.subscribe(proveedor => {
      this.proveedoreSelected = proveedor ? Array.from(proveedor) : [];
      this.resetPreviewState();
      this.debouncedPreviewCall();
    });

    this.form.controls['metodoActualizacion'].valueChanges.subscribe((value) => {
      this.debouncedPreviewCall();
    });

    this.form.controls['tipoAumento'].valueChanges.subscribe(() => {
      this.debouncedPreviewCall();
    });
    
    // Listener para el toggle de desglose (solo actualiza vista, no necesita API)
    this.form.controls['mostrarDesglose'].valueChanges.subscribe((value) => {
      // Sistema unificado - Una sola tabla con columnas dinámicas
      // No requiere llamada a API, solo cambia la vista
    });
    
    this.form.controls['porcentaje'].valueChanges.subscribe((value) => {
      // Actualizar preview cuando cambia el valor de ajuste
      this.debouncedPreviewCall();
    });

    // NUEVOS LISTENERS PARA FILTROS ESTRATÉGICOS
    this.form.controls['productType'].valueChanges.subscribe((value) => {
      this.resetPreviewState();
      this.debouncedPreviewCall();
    });

    this.form.controls['marginRangeMin'].valueChanges.subscribe((value) => {
      this.resetPreviewState();
      this.debouncedPreviewCall();
    });

    this.form.controls['marginRangeMax'].valueChanges.subscribe((value) => {
      this.resetPreviewState();
      this.debouncedPreviewCall();
    });
  }

  ngOnInit() {
    const sidebar = <HTMLElement>document.querySelector('.navbar-brand');
    if (sidebar !== undefined) {
      sidebar.innerHTML = 'Actualización Masiva de Precios';
    }
    
    // Inicializar estado de controles según valor inicial
    const tipoItemInicial = this.form.get('tipoItem')?.value;
    if (tipoItemInicial === 'servicios') {
      this.form.get('proveedor')?.disable();
      this.form.get('marca')?.disable();
    }
    
    this.getParametros();
    this.getProveedores();
    
    // Cargar preview inicial después de cargar los datos maestros
    setTimeout(() => {
      this.callPreviewAPI();
    }, 1500);
  }

  getParametros() {
    this.parametroServicio.getParametrosArticulos().subscribe(
      data => {
        this.marcas = data.marcas;
        this.rubros = data.rubros;
      }
    );
  }

  getProveedores() {
    this.clienteService.getClientesBytype('proveedor').subscribe(
      res => {
        this.proveedores = res;
      },
      err => {
        this.openSnackBar(
          `Ocurrió un error al obtener listado proveedores`,
          'Información'
        );
      }
    );
  }

  openSnackBar(message: string, action: string) {
    this._snackBar.open(message, action, {
      duration: 2000,
    });
  }

  onlyNumbers(event: KeyboardEvent): boolean {
    const charCode = event.which || event.keyCode;
    const currentValue = (event.target as HTMLInputElement).value;
    const cursorPos = (event.target as HTMLInputElement).selectionStart || 0;
    
    // Permitir teclas de control (backspace, delete, tab, escape, enter, home, end, arrows)
    if (event.ctrlKey || event.altKey || 
        charCode === 8 || charCode === 9 || charCode === 13 || charCode === 27 ||
        charCode === 35 || charCode === 36 || charCode === 37 || charCode === 38 || 
        charCode === 39 || charCode === 40 || charCode === 46 || charCode === 127) {
      return true;
    }
    
    // Permitir números (0-9)
    if (charCode >= 48 && charCode <= 57) {
      return true;
    }
    
    // Permitir signo menos (-) solo al principio del campo y si no hay ya uno
    if (charCode === 45) {
      if (cursorPos === 0 && !currentValue.includes('-')) {
        return true;
      }
    }
    
    // Permitir punto decimal (.) solo si no hay ya uno
    if (charCode === 46 || charCode === 190) {
      if (!currentValue.includes('.')) {
        return true;
      }
    }
    
    // Permitir coma decimal (,) y convertirla a punto
    if (charCode === 44 || charCode === 188) {
      if (!currentValue.includes('.') && !currentValue.includes(',')) {
        event.preventDefault();
        const input = event.target as HTMLInputElement;
        const newValue = currentValue.slice(0, cursorPos) + '.' + currentValue.slice(cursorPos);
        input.value = newValue;
        input.setSelectionRange(cursorPos + 1, cursorPos + 1);
        
        // Disparar evento de cambio
        const inputEvent = new Event('input', { bubbles: true });
        input.dispatchEvent(inputEvent);
        
        return false;
      }
    }
    
    // Bloquear cualquier otro carácter
    event.preventDefault();
    return false;
  }

  validateNumberInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Remover caracteres no válidos excepto números, punto y signo menos al inicio
    value = value.replace(/[^-0-9.]/g, '');
    
    // Asegurar que el signo menos solo esté al inicio
    if (value.includes('-')) {
      const parts = value.split('-');
      value = '-' + parts.join('');
    }
    
    // Asegurar solo un punto decimal
    const dotIndex = value.indexOf('.');
    if (dotIndex !== -1) {
      value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
    }
    
    // Validar rango (-100 a 999)
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      if (numValue < -100) {
        value = '-100';
      } else if (numValue > 999) {
        value = '999';
      }
    }
    
    // Actualizar el valor si cambió
    if (input.value !== value) {
      input.value = value;
      this.form.patchValue({ porcentaje: value });
    }
  }

  // =========================================================================
  // MÉTODOS PARA GESTIÓN DE ESTADO Y LLAMADAS A LA API DE PREVISUALIZACIÓN
  // =========================================================================

  /**
   * Resetea el estado de previsualización
   */
  private resetPreviewState(): void {
    this.previewData = null;
    this.metadata = null;
    this.previewItems = [];
    this.error = null;
    this.showPreview = false;
    this.hasApiError = false;
    this.hasEmptyResults = false;
  }

  /**
   * Llamada con debounce para optimizar las peticiones a la API
   */
  private debouncedPreviewCall(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    this.debounceTimer = setTimeout(() => {
      this.callPreviewAPI();
    }, 300); // 300ms de debounce
  }

  /**
   * Prepara el payload para la API de previsualización
   */
  private buildPreviewPayload(): any {
    const adjustmentValue = parseFloat(this.form.controls['porcentaje'].value) || 0;
    const updateMethod = this.form.get('metodoActualizacion')?.value;
    const adjustmentType = this.form.get('tipoAumento')?.value;
    const tipoItem = this.form.get('tipoItem')?.value;

    // NUEVOS FILTROS ESTRATÉGICOS
    const productType = this.form.get('productType')?.value;
    const marginMin = this.form.get('marginRangeMin')?.value;
    const marginMax = this.form.get('marginRangeMax')?.value;

    // Mapear tipoItem a itemType para el backend
    const mapItemType = (tipo: string) => {
      switch(tipo) {
        case 'productos': return 'products';
        case 'servicios': return 'services';
        case 'ambos': return 'both';
        default: return 'both';
      }
    };

    // Mapear updateMethod para el backend
    const mapUpdateMethod = (method: string) => {
      switch(method) {
        case 'precioVenta': return 'directPrice';
        case 'margenGanancia': return 'protectMargin';
        default: return method;
      }
    };

    // Mapear adjustmentType para el backend
    const mapAdjustmentUnit = (tipo: string) => {
      switch(tipo) {
        case 'porcentaje': return 'percentage';
        case 'monto': return 'fixed';
        default: return 'percentage';
      }
    };

    // Mapear productType para el backend
    const mapProductType = (tipo: string) => {
      switch(tipo) {
        case 'commercializable': return 'commercializable';
        case 'supplies': return 'supplies';
        case 'all': 
        default: return null; // null significa "todos"
      }
    };

    // Construir filters con los nuevos campos estratégicos
    const filters: any = {
      providerIds: this.proveedoreSelected.length > 0 ? this.proveedoreSelected : [],
      categoryIds: this.rubrosSelected.length > 0 ? this.rubrosSelected : [],
      brandIds: this.marcasSelected.length > 0 ? this.marcasSelected : []
    };

    // Agregar productType solo si no es "all" y el tipoItem permite productos
    const mappedProductType = mapProductType(productType);
    if (mappedProductType && this.shouldShowProductTypeFilter()) {
      filters.productType = mappedProductType;
    }

    // Agregar marginRange solo si al menos uno de los valores está definido
    if ((marginMin && marginMin !== '') || (marginMax && marginMax !== '')) {
      const minValue = marginMin && marginMin !== '' ? Number(marginMin) : null;
      const maxValue = marginMax && marginMax !== '' ? Number(marginMax) : null;
      
      // Validación adicional de consistencia antes de enviar API
      if (minValue !== null && maxValue !== null && maxValue <= minValue) {
        console.error(`❌ ERROR CRÍTICO: Rango inválido detectado en buildPreviewPayload - min: ${minValue}, max: ${maxValue}`);
        console.error('🔧 Este rango inválido causará error 400 en el backend');
        
        // Lanzar error para prevenir request inválida
        throw new Error(`Rango de margen inválido: "Hasta" (${maxValue}) debe ser mayor que "Desde" (${minValue})`);
      }
      
      filters.marginRange = {
        ...(minValue !== null && { min: minValue }),
        ...(maxValue !== null && { max: maxValue })
      };
    }

    const payload = {
      itemType: mapItemType(tipoItem),
      filters: filters,
      updateMethod: mapUpdateMethod(updateMethod),
      
      operationParams: {
        value: Number(adjustmentValue),
        unit: updateMethod === 'precioVenta' ? mapAdjustmentUnit(adjustmentType) : 'percentage'
      },
      
      pagination: {
        page: this.currentPage + 1, // API usa páginas basadas en 1
        limit: this.pageSize
      },
      
      // Incluir búsqueda si existe
      ...(this.searchQuery.trim() && {
        searchQuery: this.searchQuery.trim()
      })
    };

    return payload;
  }

  /**
   * Llama a la API de previsualización
   * SIEMPRE valida los inputs de márgenes antes de ejecutar el endpoint
   */
  private callPreviewAPI(): void {
    // PASO 1: Validación obligatoria de inputs de márgenes
    const isValid = this.validateMarginInputsBeforeEndpoint();
    
    if (!isValid) {
      return;
    }
    
    // PASO 2: Ejecutar API solo después de validación exitosa
    this.executePreviewAPI();
  }

  /**
   * Ejecuta la llamada a la API de previsualización
   */
  private executePreviewAPI(): void {
    this.isLoading = true;
    this.error = null;
    this.hasApiError = false;
    this.hasEmptyResults = false;
    
    const payload = this.buildPreviewPayload();
    
    this.articuloService.obtenerPreviewPreciosMasivos(payload).subscribe(
      (response: any) => {
        
        this.isLoading = false;
        this.previewData = response;
        this.metadata = response.metadata || {};
        this.previewItems = response.previewItems || [];
        
        // Determinar el estado según los resultados
        if (this.previewItems.length === 0) {
          this.hasEmptyResults = true;
          this.showPreview = false;
        } else {
          this.hasEmptyResults = false;
          this.showPreview = true;
        }
      },
      (error) => {
        
        this.isLoading = false;
        this.hasApiError = true;
        this.hasEmptyResults = false;
        this.error = this.processBackendErrorMessage(error);
        this.showPreview = false;
        this.previewItems = [];
        this.metadata = null;
      }
    );
  }

  // =========================================================================
  // MÉTODOS AUXILIARES PARA LA VISTA (usan datos calculados por el backend)
  // =========================================================================

  /**
   * Obtiene el total de items afectados desde los metadata de la API
   */
  getTotalItemsAfectados(): number {
    return this.metadata?.totalItems || 0;
  }

  /**
   * Verifica si hay datos de previsualización disponibles
   */
  hasPreviewData(): boolean {
    return this.previewItems && this.previewItems.length > 0;
  }

  /**
   * Calcula el porcentaje de cambio entre precio actual y nuevo
   */
  getChangePercentage(item: any): number {
    if (!item.currentPrice || item.currentPrice === 0) return 0;
    return ((item.newPrice - item.currentPrice) / item.currentPrice) * 100;
  }

  // =========================================================================
  // MÉTODOS DE PAGINACIÓN Y BÚSQUEDA (controlados por backend)
  // =========================================================================

  /**
   * Cambia la página actual y llama a la API
   */
  onPageChange(event: any): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.callPreviewAPI();
  }

  /**
   * Actualiza la búsqueda y llama a la API
   */
  onSearchChange(searchTerm: string): void {
    this.searchQuery = searchTerm;
    this.currentPage = 0; // Reset a primera página
    this.debouncedPreviewCall();
  }

  /**
   * Limpia la búsqueda
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.currentPage = 0;
    this.callPreviewAPI();
  }

  // =========================================================================
  // MÉTODOS DE FEEDBACK Y MENSAJES (usan datos de la API)
  // =========================================================================

  /**
   * Obtiene la clase CSS para el feedback de alcance basado en los datos de la API
   */
  getFeedbackClass(): string {
    const totalItems = this.getTotalItemsAfectados();
    const hasFilters = this.tieneFilters();
    
    if (hasFilters && totalItems > 0) {
      return 'alert-info'; // Azul para confirmación con filtros
    }
    
    return 'alert-warning'; // Amarillo para advertencias
  }

  /**
   * Obtiene el ícono para el feedback basado en los datos de la API
   */
  getFeedbackIcon(): string {
    const totalItems = this.getTotalItemsAfectados();
    const hasFilters = this.tieneFilters();
    
    if (hasFilters && totalItems > 0) {
      return 'info';
    }
    
    return 'warning';
  }

  /**
   * Obtiene el mensaje de feedback basado en los metadata de la API
   */
  getFeedbackMessage(): string {
    const tipoItem = this.form.get('tipoItem')?.value;
    const hasFilters = this.tieneFilters();
    const hasStrategicFilters = this.hasStrategicFiltersActive();
    const totalItems = this.getTotalItemsAfectados();
    
    // Si hay un error, mostrar mensaje de error
    if (this.error) {
      return `<strong>❌ Error:</strong> ${this.error}`;
    }
    
    // Si está cargando, mostrar mensaje de carga
    if (this.isLoading) {
      return '<strong>🔄 Calculando...</strong> Obteniendo datos del servidor...';
    }
    
    // Construir mensaje basado en metadata de la API
    let mensaje = '';
    
    if (hasFilters || hasStrategicFilters) {
      // Con filtros específicos
      let filtrosTexto: string[] = [];
      
      if (this.proveedoreSelected.length) {
        const nombres = this.proveedoreSelected.map(id => {
          const proveedor = this.proveedores.find(p => p.id === id);
          return proveedor ? proveedor.companyName : 'N/A';
        });
        filtrosTexto.push(`proveedor${this.proveedoreSelected.length > 1 ? 'es' : ''}: <em>${nombres.join(', ')}</em>`);
      }
      
      if (this.rubrosSelected.length) {
        const nombres = this.rubrosSelected.map(id => {
          const rubro = this.rubros.find(r => r.id === id);
          return rubro ? rubro.name : 'N/A';
        });
        filtrosTexto.push(`rubro${this.rubrosSelected.length > 1 ? 's' : ''}: <em>${nombres.join(', ')}</em>`);
      }
      
      if (this.marcasSelected.length) {
        const nombres = this.marcasSelected.map(id => {
          const marca = this.marcas.find(m => m.id === id);
          return marca ? marca.name : 'N/A';
        });
        filtrosTexto.push(`marca${this.marcasSelected.length > 1 ? 's' : ''}: <em>${nombres.join(', ')}</em>`);
      }

      // NUEVO: Agregar filtros estratégicos al mensaje
      if (hasStrategicFilters) {
        filtrosTexto.push(`<strong>filtros estratégicos</strong>: <em>${this.getStrategicFiltersDescription()}</em>`);
      }
      
      // Mensaje con conteo desde la API
      if (totalItems > 0) {
        mensaje = `<strong>🎯 Confirmación Estratégica:</strong> La actualización se aplicará a <strong>${totalItems}</strong> ${totalItems === 1 ? 'artículo' : 'artículos'} que coinciden con ${filtrosTexto.join(', ')}.`;
      } else {
        mensaje = `<strong>⚠️ Sin coincidencias:</strong> No se encontraron artículos que coincidan con los filtros seleccionados: ${filtrosTexto.join(', ')}.`;
      }
    } else {
      // Sin filtros - mensaje simple informativo
      if (totalItems > 0) {
        mensaje = `<strong>📊 Información:</strong> Se actualizarán <strong>${totalItems}</strong> ${totalItems === 1 ? 'artículo' : 'artículos'} del sistema.`;
      } else {
        mensaje = `<strong>⚠️ Sin datos:</strong> No se encontraron artículos para actualizar.`;
      }
    }
    
    // Agregar información adicional si no hay ítems afectados
    if (totalItems === 0 && (hasFilters || hasStrategicFilters)) {
      mensaje += '<br><small>Ajuste los filtros para incluir más productos o servicios.</small>';
    }

    // Información especial para filtros estratégicos
    if (hasStrategicFilters && totalItems > 0) {
      mensaje += '<br><small class="text-info">🎯 <strong>Filtrado Estratégico:</strong> Operación optimizada según criterios de negocio específicos</small>';
    }
    
    // Agregar información de datos en tiempo real
    if (this.metadata && !this.isLoading) {
      mensaje += '<br><small class="text-muted">✓ Datos calculados por el servidor en tiempo real</small>';
    }
    
    return mensaje;
  }

  // Métodos para manejo de filtros activos
  tieneFilters(): boolean {
    return this.proveedoreSelected.length > 0 || 
           this.rubrosSelected.length > 0 || 
           this.marcasSelected.length > 0;
  }

  getNombreProveedor(id: number): string {
    const proveedor = this.proveedores.find(p => p.id === id);
    return proveedor ? proveedor.companyName : 'N/A';
  }

  getNombreRubro(id: number): string {
    const rubro = this.rubros.find(r => r.id === id);
    return rubro ? rubro.name : 'N/A';
  }

  getNombreMarca(id: number): string {
    const marca = this.marcas.find(m => m.id === id);
    return marca ? marca.name : 'N/A';
  }

  removeProveedorFilter(id: number): void {
    this.proveedoreSelected = this.proveedoreSelected.filter(pid => pid !== id);
    this.currentPage = 0;
    this.callPreviewAPI();
  }

  removeRubroFilter(id: number): void {
    this.rubrosSelected = this.rubrosSelected.filter(rid => rid !== id);
    this.currentPage = 0;
    this.callPreviewAPI();
  }

  removeMarcaFilter(id: number): void {
    this.marcasSelected = this.marcasSelected.filter(mid => mid !== id);
    this.currentPage = 0;
    this.callPreviewAPI();
  }

  clearAllFilters(): void {
    this.proveedoreSelected = [];
    this.rubrosSelected = [];
    this.marcasSelected = [];
    this.currentPage = 0;
    this.searchQuery = '';
    this.callPreviewAPI();
  }

  /**
   * MÉTODO PRINCIPAL: Mostrar modal de confirmación antes de actualizar
   * Este método reemplaza la lógica directa de actualización
   */
  actualizar() {
    // PASO 1: VALIDACIÓN OBLIGATORIA DE INPUTS DE MÁRGENES ANTES DEL ENDPOINT
    const isValid = this.validateMarginInputsBeforeEndpoint();
    
    if (!isValid) {
      Swal.fire({
        title: 'Error de Validación',
        text: 'Los filtros de margen de ganancia contienen errores. Por favor revise los valores.',
        icon: 'error',
        confirmButtonColor: '#ef8157'
      });
      return;
    }
    
    // PASO 2: VALIDACIONES DEL PAYLOAD
    const tipoItem = this.form.get('tipoItem')?.value;
    const metodoActualizacion = this.form.get('metodoActualizacion')?.value;
    const tipoAumento = this.form.get('tipoAumento')?.value;
    let valorAjuste = parseFloat(this.form.controls['porcentaje'].value);
    
    // Validaciones previas del payload
    if (isNaN(valorAjuste)) {
      Swal.fire({
        title: 'Error de Validación',
        text: 'El valor de ajuste debe ser un número válido',
        icon: 'warning',
        confirmButtonColor: '#ef8157'
      });
      return;
    }
    
    // Validar rango según backend (-100 a 10000)
    if (valorAjuste < -100 || valorAjuste > 10000) {
      Swal.fire({
        title: 'Error de Validación',
        text: 'El valor de ajuste debe estar entre -100% y 10000',
        icon: 'warning',
        confirmButtonColor: '#ef8157'
      });
      return;
    }

    // PASO 3: MOSTRAR MODAL DE CONFIRMACIÓN (RED DE SEGURIDAD UX)
    this.mostrarModalConfirmacion();
  }

  /**
   * Muestra el modal de confirmación antes de ejecutar la actualización masiva
   * Implementa la red de seguridad de UX requerida
   */
  mostrarModalConfirmacion(): void {
    const totalItems = this.getTotalItemsAfectados();
    const valorAjuste = this.form.controls['porcentaje'].value;
    const metodoActualizacion = this.form.get('metodoActualizacion')?.value;
    
    // Construir mensaje personalizado según el método de actualización
    let metodoTexto = '';
    if (metodoActualizacion === 'precioVenta') {
      metodoTexto = 'precios de venta directos';
    } else if (metodoActualizacion === 'margenGanancia') {
      metodoTexto = 'márgenes de ganancia (remarcado inteligente)';
    }
    
    const ajusteTexto = valorAjuste > 0 ? `+${valorAjuste}%` : `${valorAjuste}%`;
    
    Swal.fire({
      title: 'Confirmar Actualización de Precios',
      html: `
        <div class="confirmation-modal-content">
          <div class="warning-icon mb-3">
            <i class="material-icons" style="font-size: 48px; color: #fbc658;">warning</i>
          </div>
          <p class="confirmation-text">
            Estás a punto de actualizar los <strong>${metodoTexto}</strong> de 
            <strong>${totalItems}</strong> artículo${totalItems === 1 ? '' : 's'} 
            con un ajuste de <strong>${ajusteTexto}</strong> de forma <em>permanente</em>.
          </p>
          <p class="confirmation-warning">
            <strong>¿Estás seguro de que deseas continuar?</strong>
          </p>
          <p class="confirmation-note">
            <em>Esta acción no se puede deshacer.</em>
          </p>
        </div>
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Confirmar y Actualizar',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#102dd3',
      cancelButtonColor: '#66615b',
      width: '500px',
      allowOutsideClick: false,
      allowEscapeKey: false,
      showLoaderOnConfirm: true,
      preConfirm: () => {
        // Ejecutar la actualización cuando el usuario confirma
        return this.ejecutarActualizacionMasiva();
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // La actualización se ejecutó correctamente (manejado en preConfirm)
        console.log('✅ Actualización completada exitosamente');
      } else if (result.isDismissed) {
        console.log('❌ Actualización cancelada por el usuario');
      }
    });
  }

  /**
   * Ejecuta la actualización masiva usando el nuevo endpoint /api/prices/execute
   * Maneja estados de UI durante la ejecución
   */
  private async ejecutarActualizacionMasiva(): Promise<any> {
    try {
      // PASO 1: CONFIGURAR ESTADOS DE UI
      this.isExecuting = true;
      this.executionProgress = 'Preparando actualización...';
      
      // PASO 2: PREPARAR PAYLOAD PARA EL NUEVO ENDPOINT
      const payload = this.buildExecutionPayload();
      
      // PASO 3: ACTUALIZAR PROGRESO
      this.executionProgress = 'Ejecutando actualización en el servidor...';
      
      // PASO 4: LLAMAR AL NUEVO ENDPOINT /api/prices/execute
      const result = await this.articuloService.ejecutarActualizacionMasiva(payload).toPromise();
      
      // PASO 5: PROCESAR RESPUESTA EXITOSA
      this.procesarRespuestaExitosa(result);
      
      return result;
      
    } catch (error) {
      // PASO 6: MANEJAR ERRORES
      console.error('❌ Error en ejecución de actualización masiva:', error);
      this.procesarErrorEjecucion(error);
      throw error; // Re-lanzar para que SweetAlert lo maneje
      
    } finally {
      // PASO 7: LIMPIAR ESTADOS DE UI
      this.isExecuting = false;
      this.executionProgress = '';
    }
  }

  /**
   * Construye el payload específico para el endpoint de ejecución
   */
  private buildExecutionPayload(): any {
    const adjustmentValue = parseFloat(this.form.controls['porcentaje'].value) || 0;
    const updateMethod = this.form.get('metodoActualizacion')?.value;
    const adjustmentType = this.form.get('tipoAumento')?.value;
    const tipoItem = this.form.get('tipoItem')?.value;

    // NUEVOS FILTROS ESTRATÉGICOS
    const productType = this.form.get('productType')?.value;
    const marginMin = this.form.get('marginRangeMin')?.value;
    const marginMax = this.form.get('marginRangeMax')?.value;

    // Mapear tipoItem a itemType para el backend
    const mapItemType = (tipo: string) => {
      switch(tipo) {
        case 'productos': return 'products';
        case 'servicios': return 'services';
        case 'ambos': return 'both';
        default: return 'both';
      }
    };

    // Mapear updateMethod para el backend
    const mapUpdateMethod = (method: string) => {
      switch(method) {
        case 'precioVenta': return 'directPrice';
        case 'margenGanancia': return 'protectMargin';
        default: return method;
      }
    };

    // Mapear adjustmentType para el backend
    const mapAdjustmentUnit = (tipo: string) => {
      switch(tipo) {
        case 'porcentaje': return 'percentage';
        case 'monto': return 'fixed';
        default: return 'percentage';
      }
    };

    // Mapear productType para el backend
    const mapProductType = (tipo: string) => {
      switch(tipo) {
        case 'commercializable': return 'commercializable';
        case 'supplies': return 'supplies';
        case 'all': 
        default: return null; // null significa "todos"
      }
    };

    // Construir filters con los nuevos campos estratégicos
    const filters: any = {
      providerIds: this.proveedoreSelected.length > 0 ? this.proveedoreSelected : [],
      categoryIds: this.rubrosSelected.length > 0 ? this.rubrosSelected : [],
      brandIds: this.marcasSelected.length > 0 ? this.marcasSelected : []
    };

    // Agregar productType solo si no es "all" y el tipoItem permite productos
    const mappedProductType = mapProductType(productType);
    if (mappedProductType && this.shouldShowProductTypeFilter()) {
      filters.productType = mappedProductType;
    }

    // NO agregar marginRange en el payload de ejecución masiva (el backend lo rechaza)

    const payload = {
      itemType: mapItemType(tipoItem),
      filters: filters,
      updateMethod: mapUpdateMethod(updateMethod),
      
      operationParams: {
        value: Number(adjustmentValue),
        unit: updateMethod === 'precioVenta' ? mapAdjustmentUnit(adjustmentType) : 'percentage'
      },
      
      // Incluir búsqueda si existe
      ...(this.searchQuery.trim() && {
        searchQuery: this.searchQuery.trim()
      })
    };

    console.log('🏗️ Payload construido para ejecución:', payload);
    return payload;
  }

  /**
   * Procesa la respuesta exitosa del servidor
   */
  private procesarRespuestaExitosa(result: any): void {
    const totalUpdated = result.totalUpdated || 0;
    const totalEvaluated = result.totalEvaluated || 0;
    const totalSkipped = result.totalSkipped || 0;
    const errors = result.errors || [];
    const message = result.message || 'Operación completada exitosamente';

    // Construir mensaje detallado
    let detailedMessage = `<div class="success-result">`;
    detailedMessage += `<p><strong>${message}</strong></p>`;
    detailedMessage += `<ul class="result-details">`;
    // Solo mostrar si hay artículos actualizados
    if (totalUpdated > 0) {
      detailedMessage += `<li>✅ <strong>${totalUpdated}</strong> artículos actualizados</li>`;
    }
    // Solo mostrar si hay artículos evaluados
    if (totalEvaluated > 0) {
      detailedMessage += `<li>📊 <strong>${totalEvaluated}</strong> artículos evaluados</li>`;
    }
    if (totalSkipped > 0) {
      detailedMessage += `<li>⏭️ <strong>${totalSkipped}</strong> artículos omitidos</li>`;
    }
    if (errors.length > 0) {
      detailedMessage += `<li>⚠️ <strong>${errors.length}</strong> errores encontrados</li>`;
    }
    detailedMessage += `</ul></div>`;

    // Mostrar notificación de éxito
    Swal.fire({
      title: '¡Actualización Completada!',
      html: detailedMessage,
      icon: 'success',
      confirmButtonColor: '#6bd098',
      confirmButtonText: 'Aceptar',
      width: '500px'
    }).then(() => {
      // Acción posterior: Refrescar la vista de previsualización
      this.refrescarVistaDespuesDeActualizacion();
    });

    // Mostrar toast adicional para feedback inmediato
    this._snackBar.open(
      `¡Éxito! Se actualizaron ${totalUpdated} precios correctamente.`,
      'Cerrar',
      {
        duration: 4000,
        panelClass: ['success-snackbar']
      }
    );

    // Log detallado para debugging
    if (errors.length > 0) {
      console.warn('⚠️ Errores específicos durante la actualización:', errors);
    }
  }

  /**
   * Procesa errores durante la ejecución
   */
  private procesarErrorEjecucion(error: any): void {
    const errorMessage = this.processBackendErrorMessage(error);
    
    // Mostrar notificación de error
    Swal.fire({
      title: 'Error en Actualización',
      html: `
        <div class="error-result">
          <p><strong>No se pudo completar la actualización de precios.</strong></p>
          <p class="error-details">${errorMessage}</p>
          <p class="error-suggestion">Por favor, revise los datos e intente de nuevo.</p>
        </div>
      `,
      icon: 'error',
      confirmButtonColor: '#ef8157',
      confirmButtonText: 'Entendido',
      width: '500px'
    });

    // Toast para error rápido
    this._snackBar.open(
      'Error: No se pudo completar la actualización. Por favor, intente de nuevo.',
      'Cerrar',
      {
        duration: 5000,
        panelClass: ['error-snackbar']
      }
    );
  }

  /**
   * Refresca la vista de previsualización después de una actualización exitosa
   * Opcional: También puede redirigir al listado de productos
   */
  private refrescarVistaDespuesDeActualizacion(): void {
    // Opción 1: Resetear formulario y cargar nueva previsualización
    this.form.patchValue({ porcentaje: 0 });
    this.resetPreviewState();
    
    // Opcional: Cargar nueva previsualización automáticamente
    setTimeout(() => {
      this.callPreviewAPI();
    }, 1000);
    
    // Opción 2: Redirigir al listado de productos (comentado - puede habilitarse según preferencia)
    // import { Router } from '@angular/router';
    // this.router.navigate(['/productos']);
  }

  /**
   * Obtiene el tooltip dinámico para el botón de actualización
   */
  getButtonTooltip(): string {
    if (this.isExecuting) {
      return 'Actualizando precios... Por favor espere.';
    }
    
    if (!this.form.valid) {
      return 'Complete todos los campos requeridos para continuar';
    }
    
    const valorAjuste = this.form.get('porcentaje')?.value;
    if (!valorAjuste || valorAjuste === 0) {
      return 'Ingrese un valor de ajuste diferente de cero';
    }
    
    const totalItems = this.getTotalItemsAfectados();
    if (totalItems === 0) {
      return 'No hay artículos para actualizar con los filtros actuales';
    }
    
    if (this.isLoading) {
      return 'Espere a que termine de cargar la previsualización';
    }
    
    const metodo = this.form.get('metodoActualizacion')?.value;
    const metodoTexto = metodo === 'precioVenta' ? 'precios de venta' : 'márgenes';
    const ajusteTexto = valorAjuste > 0 ? `+${valorAjuste}%` : `${valorAjuste}%`;
    
    return `Hacer clic para actualizar ${totalItems} ${totalItems === 1 ? 'artículo' : 'artículos'} con ajuste de ${ajusteTexto} en ${metodoTexto}`;
  }

  // =========================================================================
  // MÉTODOS AUXILIARES PARA EL TEMPLATE (usan datos de la API)
  // =========================================================================

  /**
   * Obtiene información de paginación desde metadata de la API
   */
  getPaginationInfo(): string {
    if (!this.metadata) return '';
    
    const start = (this.currentPage * this.pageSize) + 1;
    const end = Math.min((this.currentPage + 1) * this.pageSize, this.metadata.totalItems);
    const total = this.metadata.totalItems;
    
    return `Mostrando ${start} a ${end} de ${total} artículos`;
  }

  /**
   * Verifica si se debe mostrar el paginador
   */
  shouldShowPaginator(): boolean {
    return this.metadata && this.metadata.totalItems > this.pageSize;
  }

  // Métodos helper para debug del desglose
  get mostrarDesgloseValue(): boolean {
    const value = this.form.get('mostrarDesglose')?.value;
    return value;
  }

  get metodoActualizacionValue(): string {
    const value = this.form.get('metodoActualizacion')?.value;
    return value;
  }

  // Métodos para simplificar condiciones del template - ELIMINADO (duplicado)

  // =========================================================================
  // MÉTODOS PARA LA VISTA (compatibilidad con template existente)
  // =========================================================================

  /**
   * Determina si se debe mostrar la tabla de previsualización
   */
  shouldShowPreviewTable(): boolean {
    const metodo = this.form.get('metodoActualizacion')?.value;
    
    return !this.isLoading && 
           this.showPreview && 
           this.hasPreviewData() &&
           (metodo === 'precioVenta' || metodo === 'margenGanancia');
  }

  /**
   * Determina si mostrar columnas de desglose (auditoría)
   */
  mostrarDesglose(): boolean {
    return this.form.get('mostrarDesglose')?.value || false;
  }

  /**
   * Métodos de compatibilidad con template existente
   */
  shouldShowSimpleTable(): boolean {
    return this.shouldShowPreviewTable() && !this.mostrarDesglose();
  }

  shouldShowCompleteTable(): boolean {
    return this.shouldShowPreviewTable() && this.mostrarDesglose();
  }

  // =========================================================================
  // MÉTODOS PARA RENDERIZADO DIRECTO DE MÁRGENES (LÓGICA SIMPLIFICADA)
  // =========================================================================

  /**
   * Obtiene el margen actual de un item (con fallback a 0)
   */
  getCurrentMargin(item: any): number {
    return item.currentMarginPercentage || 0;
  }

  /**
   * Obtiene el margen nuevo de un item (con fallback a 0)
   */
  getNewMargin(item: any): number {
    return item.newMarginPercentage || 0;
  }

  /**
   * Calcula el número de columnas para el colspan del estado vacío
   */
  getTableColspan(): number {
    if (this.mostrarDesglose()) {
      // Vista detallada: CÓDIGO, NOMBRE, COSTO ACTUAL, COSTO NUEVO, PRECIO ACTUAL, PRECIO NUEVO, IVA, OTROS IMP, MARGEN ACTUAL, MARGEN NUEVO
      return 10;
    } else {
      // Vista simple: CÓDIGO, NOMBRE, PRECIO ACTUAL, PRECIO NUEVO, VARIACIÓN
      return 5;
    }
  }

  // =========================================================================
  // MÉTODOS PARA NUEVOS FILTROS ESTRATÉGICOS
  // =========================================================================

  /**
   * Determina si se debe mostrar el filtro de Tipo de Producto
   * Solo visible cuando tipoItem es 'productos' o 'ambos'
   */
  shouldShowProductTypeFilter(): boolean {
    const tipoItem = this.form.get('tipoItem')?.value;
    return tipoItem === 'productos' || tipoItem === 'ambos';
  }

  /**
   * Determina si el filtro de Tipo de Producto debe estar deshabilitado
   * Se deshabilita cuando tipoItem es 'servicios'
   */
  isProductTypeDisabled(): boolean {
    const tipoItem = this.form.get('tipoItem')?.value;
    return tipoItem === 'servicios';
  }

  /**
   * Validación inmediata con mensaje visual cuando se ingresa un dato
   * Se ejecuta en cada input para mostrar feedback inmediato al usuario
   */
  validateMarginInputWithMessage(event: Event, type: 'min' | 'max'): void {
    // Primero, ejecutar la validación estándar
    this.validateMarginInput(event, type);
    
    // Luego, validar rango inmediatamente y mostrar mensaje
    this.validateRangeAndShowMessage();
  }

  /**
   * Validación de rango inmediata con mensaje visual
   * Compara los valores actuales y muestra mensaje si no cumplen la condición
   */
  private validateRangeAndShowMessage(): void {
    const marginMinValue = this.form.get('marginRangeMin')?.value;
    const marginMaxValue = this.form.get('marginRangeMax')?.value;
    
    // Limpiar mensaje previo y clases de error
    this.marginRangeErrorMessage = null;
    this.removeErrorClasses();
    
    // Solo validar si ambos campos tienen valores
    if (marginMinValue && marginMinValue !== '' && marginMaxValue && marginMaxValue !== '') {
      const minNum = parseFloat(marginMinValue);
      const maxNum = parseFloat(marginMaxValue);
      
      // Verificar si los valores son números válidos
      if (isNaN(minNum) || isNaN(maxNum)) {
        this.marginRangeErrorMessage = 'Los valores deben ser números válidos';
        this.addErrorClasses();
        return;
      }
      
      // VALIDACIÓN PRINCIPAL: "Hasta" debe ser mayor que "Desde"
      if (maxNum <= minNum) {
        this.marginRangeErrorMessage = `El valor "Hasta" (${maxNum}%) debe ser mayor que "Desde" (${minNum}%)`;
        this.addErrorClasses();
        
        
        // Auto-corrección después de 2 segundos si el usuario no modifica
        setTimeout(() => {
          if (this.marginRangeErrorMessage && 
              this.form.get('marginRangeMin')?.value === marginMinValue && 
              this.form.get('marginRangeMax')?.value === marginMaxValue) {
            
            const newMaxValue = (minNum + 0.1).toFixed(1);
            this.form.patchValue({ marginRangeMax: newMaxValue });
            
            // Actualizar input visual
            const maxInput = document.querySelector('input[formControlName="marginRangeMax"]') as HTMLInputElement;
            if (maxInput) {
              maxInput.value = newMaxValue;
            }
            
            this.marginRangeErrorMessage = null;
            this.removeErrorClasses();
            
            // IMPORTANTE: NO ejecutar API aquí - esto es solo validación visual inmediata
          }
        }, 2000);
        
        return;
      }
      
      // Si llegamos aquí, el rango es válido
    }
  }

  /**
   * Agrega clases de error visual a los inputs de margen
   */
  private addErrorClasses(): void {
    const minInput = document.querySelector('input[formControlName="marginRangeMin"]') as HTMLInputElement;
    const maxInput = document.querySelector('input[formControlName="marginRangeMax"]') as HTMLInputElement;
    
    if (minInput) minInput.classList.add('margin-error');
    if (maxInput) maxInput.classList.add('margin-error');
  }

  /**
   * Remueve clases de error visual de los inputs de margen
   */
  private removeErrorClasses(): void {
    const minInput = document.querySelector('input[formControlName="marginRangeMin"]') as HTMLInputElement;
    const maxInput = document.querySelector('input[formControlName="marginRangeMax"]') as HTMLInputElement;
    
    if (minInput) minInput.classList.remove('margin-error');
    if (maxInput) maxInput.classList.remove('margin-error');
  }

  /**
   * Valida y formatea la entrada de campos de margen
   * Maneja la lógica de rango válido y limpieza de caracteres
   */
  validateMarginInput(event: Event, type: 'min' | 'max'): void {
    const input = event.target as HTMLInputElement;
    let value = input.value;
    
    // Remover caracteres no válidos (solo números, punto decimal)
    value = value.replace(/[^0-9.]/g, '');
    
    // Asegurar solo un punto decimal
    const dotIndex = value.indexOf('.');
    if (dotIndex !== -1) {
      value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1).replace(/\./g, '');
    }
    
    // Validar rango (0 a 999)
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      if (numValue < 0) {
        value = '0';
      } else if (numValue > 999) {
        value = '999';
      }
    }
    
    // Actualizar el valor si cambió
    if (input.value !== value) {
      input.value = value;
      
      // Actualizar el FormControl correspondiente
      const controlName = type === 'min' ? 'marginRangeMin' : 'marginRangeMax';
      const formValue = value === '' ? '' : value; // Mantener string vacío para consistancia
      this.form.patchValue({ [controlName]: formValue });
    }
  }

  /**
   * Validación de rango con ejecución de API - se ejecuta en blur/enter
   * Este método SÍ ejecuta la API después de validar
   */
  validateMarginRangeAndExecuteAPI(fieldType?: 'min' | 'max'): void {
    // Primero validar solo el rango (sin mensaje, ya se mostró en input)
    this.validateMarginRangeOnly();
    
    // Verificar si debe ejecutar API según la lógica específica
    const shouldExecuteAPI = this.shouldExecuteAPIForMarginField(fieldType);
    
    // Ejecutar API solo si cumple las condiciones
    if (shouldExecuteAPI && this.hasStrategicFiltersActive()) {
      this.callPreviewAPI();
    } else if (!shouldExecuteAPI) {
    }
  }
  
  /**
   * Determina si debe ejecutar la API según el campo que se está editando
   * - Para 'min': solo ejecutar si ya existe valor en 'max'
   * - Para 'max': siempre ejecutar
   * - Sin fieldType: siempre ejecutar (compatibilidad)
   */
  private shouldExecuteAPIForMarginField(fieldType?: 'min' | 'max'): boolean {
    if (!fieldType) {
      // Sin tipo específico, ejecutar siempre (compatibilidad)
      return true;
    }
    
    const marginMinValue = this.form.get('marginRangeMin')?.value;
    const marginMaxValue = this.form.get('marginRangeMax')?.value;
    
    if (fieldType === 'min') {
      // Para campo "min": solo ejecutar si ya existe valor en "max"
      const hasMaxValue = marginMaxValue && marginMaxValue !== '' && !isNaN(parseFloat(marginMaxValue));
      return hasMaxValue;
    }
    
    if (fieldType === 'max') {
      // Para campo "max": siempre ejecutar si tiene valor
      const hasMaxValue = marginMaxValue && marginMaxValue !== '' && !isNaN(parseFloat(marginMaxValue));
      return hasMaxValue;
    }
    
    return true; // Fallback
  }

  /**
   * Validación de rango de márgenes - SOLO valida, NO ejecuta API
   * Usado para validación interna sin efectos secundarios
   */
  private validateMarginRangeOnly(): void {
    const marginMinControl = this.form.get('marginRangeMin');
    const marginMaxControl = this.form.get('marginRangeMax');
    
    const marginMinValue = marginMinControl?.value;
    const marginMaxValue = marginMaxControl?.value;
    
    
    // Solo validar si ambos campos tienen valores válidos
    if (marginMinValue && marginMinValue !== '' && marginMaxValue && marginMaxValue !== '') {
      const minNum = parseFloat(marginMinValue);
      const maxNum = parseFloat(marginMaxValue);
      
      
      // Si "Hasta" es menor o igual que "Desde", ajustar "Hasta"
      if (!isNaN(minNum) && !isNaN(maxNum) && maxNum <= minNum) {
        const newMaxValue = (minNum + 0.1).toFixed(1); // Incremento más pequeño
        
        
        // Actualizar FormControl
        marginMaxControl?.patchValue(newMaxValue);
        
        // También actualizar el input visual
        const maxInput = document.querySelector('input[formControlName="marginRangeMax"]') as HTMLInputElement;
        if (maxInput) {
          maxInput.value = newMaxValue;
        }
        
        return;
      } else if (!isNaN(minNum) && !isNaN(maxNum)) {
      }
    } else {
    }
  }

  /**
   * Validación de rango de márgenes - LEGACY METHOD (mantener para compatibilidad)
   * DEPRECATED: Usar validateMarginRangeAndExecuteAPI() en su lugar
   */
  validateMarginRange(): void {
    this.validateMarginRangeAndExecuteAPI();
  }

  /**
   * VALIDACIÓN OBLIGATORIA: Valida inputs de márgenes antes de cualquier llamada al endpoint
   * Este método DEBE ejecutarse antes de toda llamada API para prevenir errores 400
   */
  private validateMarginInputsBeforeEndpoint(): boolean {
    const marginMinControl = this.form.get('marginRangeMin');
    const marginMaxControl = this.form.get('marginRangeMax');
    
    const marginMinValue = marginMinControl?.value;
    const marginMaxValue = marginMaxControl?.value;
    
    
    // Si no hay valores de margen, la validación pasa (filtros opcionales)
    if ((!marginMinValue || marginMinValue === '') && (!marginMaxValue || marginMaxValue === '')) {
      return true;
    }
    
    // Si solo hay un valor, la validación pasa
    if ((!marginMinValue || marginMinValue === '') || (!marginMaxValue || marginMaxValue === '')) {
      return true;
    }
    
    // Si ambos valores están presentes, validar el rango
    const minNum = parseFloat(marginMinValue);
    const maxNum = parseFloat(marginMaxValue);
    
    
    // Verificar si los valores son números válidos
    if (isNaN(minNum) || isNaN(maxNum)) {
      console.error('❌ ERROR: Valores de margen no son números válidos');
      return false;
    }
    
    // VALIDACIÓN CRÍTICA: "Hasta" debe ser mayor que "Desde"
    if (maxNum <= minNum) {
      console.error('❌ ERROR CRÍTICO: Rango de margen inválido detectado');
      console.error(`📊 Desde=${minNum}, Hasta=${maxNum} (Hasta ≤ Desde)`);
      
      // CORRECCIÓN AUTOMÁTICA
      const newMaxValue = (minNum + 0.1).toFixed(1);
      
      // Actualizar FormControl
      marginMaxControl?.patchValue(newMaxValue);
      
      // Actualizar input visual
      const maxInput = document.querySelector('input[formControlName="marginRangeMax"]') as HTMLInputElement;
      if (maxInput) {
        maxInput.value = newMaxValue;
      }
      
      
      // Dar tiempo para que se actualicen los controles
      return true;
    }
    
    return true;
  }

  /**
   * Validación adicional antes de enviar la API (MÉTODO LEGACY - MANTENER PARA COMPATIBILIDAD)
   * Se ejecuta justo antes de construir el payload para asegurar consistencia
   */
  private validateMarginRangeBeforeAPI(): boolean {
    const marginMinValue = this.form.get('marginRangeMin')?.value;
    const marginMaxValue = this.form.get('marginRangeMax')?.value;
    
    if (marginMinValue && marginMinValue !== '' && marginMaxValue && marginMaxValue !== '') {
      const minNum = parseFloat(marginMinValue);
      const maxNum = parseFloat(marginMaxValue);
      
      if (!isNaN(minNum) && !isNaN(maxNum) && maxNum <= minNum) {
        console.error(`❌ ERROR: Rango inválido detectado antes de API: Desde=${minNum}, Hasta=${maxNum}`);
        
        // Corregir automáticamente
        const newMaxValue = (minNum + 1).toString();
        this.form.patchValue({ marginRangeMax: newMaxValue });
        
        // Actualizar input visual
        const maxInput = document.querySelector('input[formControlName="marginRangeMax"]') as HTMLInputElement;
        if (maxInput) {
          maxInput.value = newMaxValue;
        }
        
        return true; // Indica que hubo corrección
      }
    }
    
    return false; // Sin correcciones necesarias
  }

  /**
   * Verifica si hay filtros estratégicos activos (productType o marginRange)
   */
  hasStrategicFiltersActive(): boolean {
    const productType = this.form.get('productType')?.value;
    const marginMin = this.form.get('marginRangeMin')?.value;
    const marginMax = this.form.get('marginRangeMax')?.value;
    
    return (productType && productType !== 'all') || 
           (marginMin && marginMin !== '') || 
           (marginMax && marginMax !== '');
  }

  /**
   * Obtiene el texto descriptivo de los filtros estratégicos activos
   */
  getStrategicFiltersDescription(): string {
    const activeFilters: string[] = [];
    
    const productType = this.form.get('productType')?.value;
    if (productType && productType !== 'all') {
      const typeText = productType === 'commercializable' ? 'Comercializables' : 'Insumos';
      activeFilters.push(`Tipo: ${typeText}`);
    }
    
    const marginMin = this.form.get('marginRangeMin')?.value;
    const marginMax = this.form.get('marginRangeMax')?.value;
    
    if ((marginMin && marginMin !== '') || (marginMax && marginMax !== '')) {
      let rangeText = 'Margen: ';
      if (marginMin && marginMin !== '' && marginMax && marginMax !== '') {
        rangeText += `${marginMin}% - ${marginMax}%`;
      } else if (marginMin && marginMin !== '') {
        rangeText += `desde ${marginMin}%`;
      } else if (marginMax && marginMax !== '') {
        rangeText += `hasta ${marginMax}%`;
      }
      activeFilters.push(rangeText);
    }
    
    return activeFilters.join(', ');
  }

  /**
   * Obtiene descripción completa con conteo de resultados para el badge
   */
  getStrategicFiltersDescriptionWithCount(): string {
    const totalItems = this.getTotalItemsAfectados();
    const description = this.getStrategicFiltersDescription();
    
    if (totalItems > 0 && description) {
      return `${totalItems} art. | ${description}`;
    } else if (description) {
      return description;
    }
    
    return '';
  }

  /**
   * Obtiene descripción detallada para la caja de filtros activos
   */
  getDetailedFiltersDescription(): string {
    const totalItems = this.getTotalItemsAfectados();
    const description = this.getStrategicFiltersDescription();
    
    if (totalItems > 0 && description) {
      return `Se encontraron ${totalItems} artículo${totalItems === 1 ? '' : 's'} | ${description}`;
    } else if (description) {
      return `${description} (calculando...)`;
    }
    
    return 'Filtros aplicados';
  }

  /**
   * Limpia solo los filtros estratégicos (productType y marginRange)
   * Mantiene los otros filtros (proveedor, rubro, marca) intactos
   */
  clearStrategicFilters(): void {
    this.form.patchValue({
      productType: 'all',
      marginRangeMin: '',
      marginRangeMax: ''
    });
    
    // Resetear página y llamar API
    this.currentPage = 0;
    this.callPreviewAPI();
  }

  /**
   * Obtiene el mensaje de estado para la caja de resultados según el estado actual
   */
  getResultBoxMessage(): string {
    if (this.isLoading) {
      return '🔄 Buscando artículos...';
    }
    
    if (this.hasApiError) {
      return 'Error de conexión al servidor';
    }
    
    if (this.hasEmptyResults) {
      return 'ℹ️ 0 artículos encontrados.';
    }
    
    const totalItems = this.getTotalItemsAfectados();
    if (totalItems > 0) {
      return `✅ Se encontraron ${totalItems} artículo${totalItems === 1 ? '' : 's'} que coinciden con los filtros aplicados.`;
    }
    
    return '';
  }

  /**
   * Determina si debe mostrarse la caja de resultados
   */
  shouldShowResultBox(): boolean {
    return this.hasStrategicFiltersActive() && (this.isLoading || this.hasApiError || this.hasEmptyResults || this.previewItems.length > 0);
  }

  /**
   * Procesa los mensajes de error del backend para mostrar información más útil al usuario
   */
  processBackendErrorMessage(error: any): string {
    
    // Si hay un mensaje específico del backend
    if (error?.error?.message) {
      const backendMessage = error.error.message;
      
      // Casos específicos de errores de negocio
      if (backendMessage.includes('No se pudieron procesar los artículos seleccionados')) {
        return 'Los artículos seleccionados no tienen costos y precios válidos definidos. Verifique la configuración de productos.';
      }
      
      if (backendMessage.includes('Error generando previsualización')) {
        return 'Error al generar la previsualización. ' + this.extractUsefulMessage(backendMessage);
      }
      
      if (backendMessage.includes('artículos seleccionados')) {
        return this.extractUsefulMessage(backendMessage);
      }
      
      // Si es un mensaje técnico, extraer la parte útil
      return this.extractUsefulMessage(backendMessage);
    }
    
    // Errores de red/conexión
    if (error?.status === 0 || error?.name === 'HttpErrorResponse') {
      return 'Error de conexión al servidor. Verifique su conexión a internet e intente nuevamente.';
    }
    
    // Errores HTTP específicos
    if (error?.status === 400) {
      return 'Error en la solicitud. Verifique que los datos ingresados sean correctos.';
    }
    
    if (error?.status === 500) {
      return 'Error interno del servidor. Por favor, intente nuevamente en unos momentos.';
    }
    
    // Error genérico
    return 'Error al procesar la solicitud. Por favor, intente nuevamente.';
  }

  /**
   * Extrae la parte útil del mensaje de error, removiendo prefijos técnicos
   */
  private extractUsefulMessage(message: string): string {
    // Remover prefijos técnicos comunes
    let cleanMessage = message
      .replace(/^Error generando previsualización:\s*/i, '')
      .replace(/^Error:\s*/i, '')
      .replace(/^Bad Request:\s*/i, '');
    
    // Capitalizar primera letra
    cleanMessage = cleanMessage.charAt(0).toUpperCase() + cleanMessage.slice(1);
    
    // Asegurar que termine con punto
    if (!cleanMessage.endsWith('.')) {
      cleanMessage += '.';
    }
    
    return cleanMessage;
  }

  // =========================================================================
  // MÉTODOS PARA LA NUEVA INTERFAZ REORGANIZADA Y COLAPSABLE
  // =========================================================================

  /**
   * Alterna el estado de expansión de los filtros estratégicos
   */
  toggleStrategicFilters(): void {
    this.isStrategicFiltersExpanded = !this.isStrategicFiltersExpanded;
  }

  /**
   * Determina si se debe mostrar la caja de confirmación dinámica
   * Se muestra cuando hay algún filtro aplicado o hay datos cargados
   */
  shouldShowConfirmationBox(): boolean {
    const hasAnyFilters = this.tieneFilters() || this.hasStrategicFiltersActive();
    const hasSearchQuery = this.searchQuery && this.searchQuery.trim().length > 0;
    const hasPreviewData = this.showPreview || this.isLoading;
    
    return hasAnyFilters || hasSearchQuery || hasPreviewData;
  }

  /**
   * Obtiene el mensaje específico para la caja de confirmación
   * Más conciso que el feedback general
   */
  getConfirmationMessage(): string {
    const totalItems = this.getTotalItemsAfectados();
    const hasFilters = this.tieneFilters() || this.hasStrategicFiltersActive();
    
    // Si está cargando
    if (this.isLoading) {
      return 'Calculando artículos que coinciden con los filtros seleccionados...';
    }
    
    // Si hay error REAL de API (conexión, servidor, etc.)
    if (this.hasApiError && this.error) {
      return this.error;
    }
    
    // Con filtros y resultados
    if (hasFilters && totalItems > 0) {
      return `Se han encontrado <strong>${totalItems}</strong> ${totalItems === 1 ? 'artículo que coincide' : 'artículos que coinciden'} con los filtros seleccionados.`;
    }
    
    // Con filtros pero sin resultados (NO ES UN ERROR, es un resultado válido)
    if (hasFilters && (totalItems === 0 || this.hasEmptyResults)) {
      return 'No se encontraron artículos que coincidan con los filtros seleccionados. Pruebe ajustar o eliminar algún filtro para ver más resultados.';
    }
    
    // Sin filtros específicos
    if (totalItems > 0) {
      return `Se procesarán <strong>${totalItems}</strong> ${totalItems === 1 ? 'artículo' : 'artículos'} del sistema.`;
    }
    
    // Default
    return 'Configure los filtros para ver qué artículos serán actualizados.';
  }

  /**
   * Obtiene el mensaje de ayuda apropiado según el tipo de error
   */
  getErrorHelpMessage(): string {
    if (!this.error) {
      return 'Verifique su conexión y vuelva a intentar';
    }

    // Si el error menciona rangos de margen, es un error de filtro
    if (this.error.toLowerCase().includes('margen') && 
        (this.error.includes('entre') || this.error.includes('disponibles'))) {
      return 'Ajuste el rango de margen según los valores sugeridos';
    }

    // Si el error menciona filtros o no se encontraron artículos
    if (this.error.toLowerCase().includes('no se encontraron') || 
        this.error.toLowerCase().includes('filtro')) {
      return 'Modifique o elimine algunos filtros para obtener resultados';
    }

    // Si el error menciona validación o bad request
    if (this.error.toLowerCase().includes('validación') || 
        this.error.toLowerCase().includes('bad request')) {
      return 'Revise los datos ingresados y corrija los valores indicados';
    }

    // Error genérico de conexión o servidor
    return 'Verifique su conexión y vuelva a intentar';
  }

}
